'use client';

import { NextUIProvider } from '@nextui-org/system';
import * as Sentry from '@sentry/nextjs';
import { createStore, Provider as JotaiProvider } from 'jotai';
import { jwtDecode } from 'jwt-decode';
import { usePathname } from 'next/navigation';
import { SessionProvider, useSession } from 'next-auth/react';
import { useEffect } from 'react';

import { ApolloSSRProvider } from '@/apollo/ssr-client';
import MaintenanceChecker from '@/components/MaintenanceChecker';
import { setCurrentIdToken } from '@/lib/auth/token-bridge';

// jotaiのatomを初期化して保持するためのストア
const jotaiStore = createStore();

// Service Workerの更新チェックとキャッシュクリア
function useServiceWorkerUpdate() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    // 開発環境: 本番SWが残っているとwebpackチャンクを横取りしてエラーになるため解除
    if (process.env.NODE_ENV === 'development') {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(r => r.unregister());
      });
      return;
    }

    // 本番環境: SW更新チェックとアクティベーション
    const checkForUpdates = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
          await registration.update();
        }
      } catch (error) {
        console.error('Service Worker update check failed:', error);
      }
    };

    checkForUpdates();

    const handleControllerChange = () => {
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <SessionProvider>
      <InnerProviders>{children}</InnerProviders>
    </SessionProvider>
  );
}

export function InnerProviders({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const pathname = usePathname();

  // Admin画面ではメンテナンスチェックをスキップ
  const isAdminPath = pathname?.startsWith('/admin');

  // Service Workerの更新を監視
  useServiceWorkerUpdate();

  // session（SSOT）の idToken を Apollo link 用の module ref に同期する。
  // これにより Apollo link は getSession()（HTTP fetch）を呼ばずに最新トークンを読め、
  // update()/再ログインで session が更新されれば即座に最新が反映される。
  useEffect(() => {
    if (session?.user?.idToken) {
      setCurrentIdToken(session.user.idToken);

      // Decode JWT to get user info and set Sentry user context
      try {
        const decoded = jwtDecode<{ sub: string; email?: string }>(session.user.idToken);
        Sentry.setUser({
          id: decoded.sub,
          email: decoded.email,
        });
      } catch (error) {
        console.error('Failed to decode JWT token:', error);
        // JWT認証エラーは重要なセキュリティイベントなのでSentryに送信
        Sentry.captureException(error, {
          tags: { errorType: 'jwt_decode_error' },
          level: 'error',
        });
      }
    } else {
      // ログアウト時はトークン参照と Sentry ユーザーコンテキストをクリア
      setCurrentIdToken(null);
      Sentry.setUser(null);
    }
  }, [session?.user?.idToken]);

  return (
    <JotaiProvider store={jotaiStore}>
      <ApolloSSRProvider>
        <NextUIProvider>{isAdminPath ? children : <MaintenanceChecker>{children}</MaintenanceChecker>}</NextUIProvider>
      </ApolloSSRProvider>
    </JotaiProvider>
  );
}
